import { formatMoney } from "//valyoufurniture.com/cdn/shop/t/130/assets/theme.js?v=138020587941745085911790684229";

/**
 * Live updates for the tiered "spend X, save Y" discount bar.
 *
 * Liquid renders the complete initial state (see snippets/tiered-discount-bar.liquid), so the cart page - which
 * reloads in full on every quantity change and dispatches no event - needs no JS at all. This element only keeps the
 * bar in sync inside the cart drawer, where `.cart-drawer__top` survives the drawer's partial refresh.
 *
 * Money values are integers in the smallest unit of the presentment currency, matching the Cart AJAX API.
 */
class TieredDiscountBar extends HTMLElement {
  static get observedAttributes() {
    return ["subtotal"];
  }

  /** @param {CustomEvent} event */
  #onCartChanged = (event) => {
    const cart = event.detail?.cart;

    // The optimistic dispatch fired right after a product form add only carries {item_count, sections}. A corrective
    // dispatch with the full cart follows shortly after, so wait for it instead of writing NaN into the bar.
    if (typeof cart?.items_subtotal_price !== "number") {
      return;
    }

    this.setAttribute("subtotal", cart.items_subtotal_price);
  };

  connectedCallback() {
    document.addEventListener("cart:change", this.#onCartChanged);
    this.#update();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.#onCartChanged);
  }

  attributeChangedCallback() {
    this.#update();
  }

  /** @returns {number} */
  get subtotal() {
    return parseFloat(this.getAttribute("subtotal")) || 0;
  }

  /**
   * Tiers in presentment cents. The attribute only ever carries pairs the snippet already validated as positive and
   * strictly ascending, and the rate is a positive multiplier, so the guard below never drops one of those - the tier
   * dots stay matched to this array by index. It still rejects a hand-edited or empty attribute rather than claiming
   * a $0.00 discount. The rate is applied on read and never written back, so re-connecting can never compound it.
   *
   * @returns {Object[]} One entry per tier, each carrying a numeric `spend` and `save`.
   */
  get tiers() {
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;

    return (this.getAttribute("tiers") || "")
      .split(",")
      .map((pair) => {
        const [spend, save] = pair.split(":").map((value) => Math.round(parseFloat(value) * rate));

        return { spend, save };
      })
      .filter((tier) => tier.spend > 0 && tier.save > 0);
  }

  async #update() {
    await window.customElements.whenDefined("progress-bar");

    // State is read after the await, so concurrent updates all converge on the latest attribute values.
    const tiers = this.tiers;
    const messageElement = this.querySelector(":scope > span");

    if (tiers.length === 0 || !messageElement) {
      return;
    }

    // Mirrors the Liquid snippet exactly: highest reached tier, first unreached tier, largest configured tier.
    const subtotal = this.subtotal;
    const maxSpend = Math.max(...tiers.map((tier) => tier.spend));
    const achievedTier = tiers.filter((tier) => subtotal >= tier.spend).pop();
    const nextTier = tiers.find((tier) => tier.spend > subtotal);

    const progressBarElement = this.querySelector("progress-bar");

    if (progressBarElement) {
      progressBarElement.valueMax = maxSpend;
      progressBarElement.valueNow = Math.min(subtotal, maxSpend);
    }

    for (const [index, dotElement] of this.querySelectorAll(".tier-dot").entries()) {
      if (tiers[index]) {
        dotElement.classList.toggle("is-reached", subtotal >= tiers[index].spend);
      }
    }

    // Mirrors the emphasis the Liquid snippet captures for the server-rendered message. All three amounts are always
    // defined, exactly as the snippet defines all three captures, so a translation carrying a placeholder its branch
    // does not use resolves identically on both paths instead of diverging.
    const money = (cents) => `<span class="bold text-accent">${formatMoney(cents)}</span>`;
    const values = {
      remaining_amount: money(nextTier ? Math.max(nextTier.spend - subtotal, 0) : 0),
      discount_amount: money(nextTier ? nextTier.save : 0),
      saved_amount: money(achievedTier ? achievedTier.save : 0)
    };

    let template;

    if (!achievedTier) {
      template = this.getAttribute("below-first-message");
    } else if (nextTier) {
      template = this.getAttribute("between-message");
    } else {
      template = this.getAttribute("max-message");
    }

    if (!template) {
      return;
    }

    // The pattern matches the snippet's literal `replace` exactly - single spaces inside the braces - so a malformed
    // placeholder survives verbatim on both paths rather than being silently repaired on one of them. Replacing via
    // callback keeps "$" literal, since a string replacement would read "$&" and friends as backreferences (that is
    // why FreeShippingBar needs its $$$$ workaround). Escaping the braces as \{\{ also stops Liquid from parsing
    // them, which is why this file needs no raw tag around its body.
    const message = template.replace(/\{\{ (\w+) \}\}/g, (match, key) => (key in values ? values[key] : match));

    // Only write on a real change: this method runs on upgrade and again on connect, and a redundant write to the
    // aria-live region would make screen readers announce the bar on page load.
    if (messageElement.innerHTML !== message) {
      messageElement.innerHTML = message;
    }
  }
}

if (!window.customElements.get("tiered-discount-bar")) {
  window.customElements.define("tiered-discount-bar", TieredDiscountBar);
}
